import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { MixpanelContext } from "../../../context/AnalyticsService";
import { NavigationId } from "../../../navigation/AppParamList";
import FullScreenContainer from "../../../components/Containers/FullScreenContainer";
import RectangleButton from "../../../components/Buttons/RectangleButton";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import {
  CloneEventDetails,
  Event,
  EventType,
  NotificationType,
  OnboardingFlowType,
} from "@markit/common.types";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import {
  createEventData,
  updateEventData,
} from "../../../redux/slices/eventSlice";
import { makeEmptyEvent } from "../../../utils/makeEvent";
import { addToCurrUserWishList } from "../../../redux/slices/wishMarkSlice";
import CustomRichTextEditor from "../../../components/TextFields/CustomRichTextEditor";
import CreateProductDetails from "../../../components/CreatorDashboard/Products/CreateProduct/CreateProductDetails";
import CreateProductPublished from "../../../components/CreatorDashboard/Products/CreateProduct/CreateProductPublished";
import {
  useEventSelector,
  useParametricSelector,
} from "../../../hooks/useParametricSelector";
import { getLoadedEvent } from "../../../redux/slices/dataSlice";
import LoadingScreen from "../../LoadingScreen";
import { useLocation, useNavigate } from "react-router-dom";
import isEqual from "lodash.isequal";
import ConfirmDeleteModal from "../../../components/Containers/ConfirmPopups/ConfirmDeleteModal";
import { saveMediaToFirestore } from "../../../utils/photoUtils";
import { generate } from "shortid";
import { useRichTextEditor } from "../../../hooks/useRichTextEditor";
import CreateProductTitleDescription from "../../../components/CreatorDashboard/Products/CreateProduct/CreateProductTitleDescription";
import { validateProductBody } from "../../../utils/productUtils";
import {
  getProductBaseStoragePath,
  isFirebaseStorageUrl,
} from "@markit/common.utils";
import OnboardingTourStep from "../../../components/Onboarding/OnboardingTourStep";
import { NEWSLETTER_BLOG_ONBOARDING_STEPS } from "../../../utils/onboardingFlowUtils";

enum CreatePostStage {
  POST = "Post",
  DETAILS = "Details",
  PUBLISHED = "Published",
}

const CreatePost = () => {
  const { accountData } = useSelector(getAccountState).account;
  const { state } = useLocation();
  const { duplicatedEvent, cloneDetails } = (state || {}) as {
    duplicatedEvent?: Event;
    cloneDetails?: CloneEventDetails;
  };
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);
  const [stage, setStage] = useState<CreatePostStage>(CreatePostStage.POST);
  const [tempPostData, setTempPostData] = useState<Event>(
    duplicatedEvent
      ? { ...duplicatedEvent, eventType: EventType.POST }
      : makeEmptyEvent(
          accountData.uid,
          accountData.isAdmin,
          undefined,
          EventType.POST
        )
  );
  const [loadingContent, setLoadingContent] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmBackModal, setShowConfirmBackModal] = useState(false);
  const [editorChanged, setEditorChanged] = useState(false);
  const editorChangedRef = useRef(false);

  const styles = {
    borderButton: { padding: 14, border: `0.5px solid ${Colors.GRAY2}` },
    inputContainer: {
      color: Colors.BLACK,
      border: "none",
      resize: "none",
      padding: 0,
    },
  };

  const productId = useEventSelector();
  const effectiveProductId = duplicatedEvent ? "" : productId;
  const {
    isLoading: isLoadingPostData,
    data: postData,
    isError: isErrorPostData,
  } = useParametricSelector(getLoadedEvent, effectiveProductId);
  const isEditing = useMemo(
    () => effectiveProductId !== "",
    [effectiveProductId]
  );

  const { editor } = useRichTextEditor({
    body: postData?.body,
    styles: "",
    addImageExtension: true,
  });

  // Track any changes in the content
  useEffect(() => {
    if (editor) {
      const handleUpdate = () => {
        if (!loadingContent && !editorChangedRef.current) {
          editorChangedRef.current = true;
          setEditorChanged(true);
        }
      };

      editor.on("update", handleUpdate);

      return () => {
        editor.off("update", handleUpdate);
      };
    }
  }, [editor, loadingContent]);

  useEffect(() => {
    if (postData) {
      setTempPostData(postData);
      if (editor && postData.body) {
        editor.commands.setContent(postData.body);
      }
      setLoadingContent(false);
      // Reset editor change tracking when loading new post data
      editorChangedRef.current = false;
      setEditorChanged(false);
    }
  }, [postData, editor, productId]);

  useEffect(() => {
    if (!isEditing) {
      setLoadingContent(false);
    }
    if (editor && duplicatedEvent?.body && !isEditing) {
      editor.commands.setContent(duplicatedEvent.body);
    }
  }, [duplicatedEvent?.body, editor, isEditing]);

  const madeChanges = useMemo(
    () => isEditing && (!isEqual(postData, tempPostData) || editorChanged),
    [isEditing, postData, tempPostData, editorChanged]
  );

  const backOnPress = useCallback(() => {
    stage === CreatePostStage.DETAILS
      ? setStage(CreatePostStage.POST)
      : stage === CreatePostStage.PUBLISHED || isEditing
        ? isEditing
          ? navigate(-1)
          : navigate(`/e/${tempPostData.id}/dashboard`, {
              state: { eventId: tempPostData.id },
            })
        : navigate(NavigationId.NEW_PRODUCT);
  }, [isEditing, navigate, stage, tempPostData.id]);

  const saveImages = useCallback(async () => {
    const finalBody = editor.getJSON();
    const foundImageNodes = finalBody.content.filter(
      (node) => node.type === "image"
    );
    for (let i = 0; i < foundImageNodes.length; i++) {
      const imageAttrs = foundImageNodes[i].attrs;
      if (imageAttrs && !isFirebaseStorageUrl(imageAttrs.src)) {
        const url = await saveMediaToFirestore(
          imageAttrs.src,
          getProductBaseStoragePath(accountData.uid, tempPostData.id) +
            `/${generate()}`,
          ""
        );
        imageAttrs.src = url;
        foundImageNodes[i].attrs = imageAttrs;
      }
    }
    return finalBody;
  }, [accountData.uid, editor, tempPostData.id]);

  const publishOnPress = useCallback(async () => {
    const finalBody = await saveImages();
    const finalPost: Event = {
      ...tempPostData,
      body: finalBody,
    };
    setIsSaving(true);
    const dispatchPromises = [
      dispatch(createEventData(finalPost, mixpanel, false, cloneDetails)),
      dispatch(addToCurrUserWishList(finalPost.id, accountData.uid)),
    ];
    await Promise.all(dispatchPromises);

    showNotificationBanner(
      dispatch,
      "Post Created!",
      NotificationType.AFFIRMATIVE
    );
    setStage(CreatePostStage.PUBLISHED);
    setIsSaving(false);
  }, [
    accountData.uid,
    dispatch,
    mixpanel,
    saveImages,
    tempPostData,
    cloneDetails,
  ]);

  const updatePostOnPress = useCallback(async () => {
    const finalBody = await saveImages();
    const finalPost: Event = {
      ...tempPostData,
      body: finalBody,
    };
    setIsSaving(true);
    await dispatch(updateEventData(finalPost));
    showNotificationBanner(
      dispatch,
      "Post Updated!",
      NotificationType.AFFIRMATIVE
    );
    navigate(`/e/${tempPostData.id}/dashboard`, {
      state: { eventId: tempPostData.id },
    });
    setIsSaving(false);
  }, [dispatch, navigate, saveImages, tempPostData]);

  const continueOnPress = useCallback(async () => {
    if (editor.isEmpty) {
      alert("You must contain content on your post.");
      return;
    }

    if (!validateProductBody(editor)) {
      return;
    }

    if (isEditing) {
      updatePostOnPress();
    } else {
      setStage(CreatePostStage.DETAILS);
    }
  }, [editor, isEditing, updatePostOnPress]);

  const doneOnPress = useCallback(() => {
    navigate(`/e/${tempPostData.id}/dashboard`, {
      state: { eventId: tempPostData.id },
    });
  }, [navigate, tempPostData.id]);

  if (isEditing && (isLoadingPostData || loadingContent)) {
    return <LoadingScreen isLoadingPage={isLoadingPostData} />;
  }

  if (isEditing && (isErrorPostData || (!isLoadingPostData && !postData))) {
    return <LoadingScreen error />;
  }

  return (
    <FullScreenContainer
      title={
        stage === CreatePostStage.POST
          ? isEditing
            ? "Edit Post"
            : "Customize"
          : stage === CreatePostStage.DETAILS
            ? isEditing
              ? "Settings"
              : "Publish"
            : "Published"
      }
      helmetTitle={isEditing ? "Edit Post" : "Create Post"}
      backOnPress={() => {
        if (stage === CreatePostStage.POST && madeChanges) {
          setShowConfirmBackModal(true);
          return;
        }
        backOnPress();
      }}
      alternateLeftIcon="ion:arrow-back"
      rightHeaderComp={
        <div className="AlignedRow" style={{ gap: 10 }}>
          {isEditing && stage === CreatePostStage.POST ? (
            <RectangleButton
              buttonLabel="Settings"
              onPress={() => setStage(CreatePostStage.DETAILS)}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              containerStyles={styles.borderButton}
              iconLeft={
                <Icon
                  icon="ion:cog"
                  height={19}
                  style={{ color: Colors.BLACK }}
                />
              }
            />
          ) : stage === CreatePostStage.PUBLISHED ? (
            <RectangleButton
              buttonLabel="New Product"
              onPress={() => navigate(NavigationId.NEW_PRODUCT)}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              containerStyles={{
                whiteSpace: "nowrap",
                ...styles.borderButton,
              }}
            />
          ) : null}
          <div>
            <RectangleButton
              buttonLabel={
                stage === CreatePostStage.POST
                  ? isEditing
                    ? "Save"
                    : "Continue"
                  : stage === CreatePostStage.DETAILS
                    ? isEditing
                      ? "Done"
                      : "Publish"
                    : "Done"
              }
              onPress={
                stage === CreatePostStage.POST
                  ? continueOnPress
                  : stage === CreatePostStage.DETAILS
                    ? isEditing
                      ? () => setStage(CreatePostStage.POST)
                      : publishOnPress
                    : doneOnPress
              }
              altColor={
                stage === CreatePostStage.DETAILS && !isEditing
                  ? Colors.BLUE5
                  : Colors.BLACK
              }
              disabled={tempPostData.title === ""}
              loading={isSaving}
              altTextColor={Colors.WHITE}
              containerStyles={{ padding: 14 }}
              iconRight={
                stage === CreatePostStage.POST && !isEditing ? (
                  <Icon
                    icon="ion:arrow-forward"
                    height={19}
                    style={{
                      color:
                        tempPostData.title === "" ? Colors.GRAY2 : Colors.WHITE,
                    }}
                  />
                ) : undefined
              }
            />
          </div>
        </div>
      }
    >
      {stage === CreatePostStage.POST ? (
        <div
          className="ColumnNormal"
          style={{
            gap: 24,
            marginInline: "auto",
            minHeight: 700,
            width: 700,
            paddingBottom: 100,
          }}
        >
          <div className="onboarding-post-customize-target">
            <CustomRichTextEditor
              editor={editor}
              headerComp={
                <CreateProductTitleDescription
                  tempProductData={tempPostData}
                  setTempProductData={setTempPostData}
                  titlePlaceholder="Title..."
                  descriptionPlaceholder="Add a subtitle..."
                />
              }
            />
          </div>
          <OnboardingTourStep
            key="post-tour-step-2"
            flowType={OnboardingFlowType.NEWSLETTER_BLOG}
            target=".onboarding-post-customize-target"
            content={
              <>
                Here is the drafting screen where you customize your post.
                <br />
                <br />
                You can add a title and subtitle, upload images, insert links,
                and format your content using different font styles and layout
                options.
              </>
            }
            stepNumber={2}
            totalSteps={NEWSLETTER_BLOG_ONBOARDING_STEPS}
            placement="bottom"
            skipScroll
          />
        </div>
      ) : stage === CreatePostStage.DETAILS ? (
        <>
          <CreateProductDetails
            productDataSettings={tempPostData}
            updateProductDataSettings={(update: Partial<Event>) => {
              setTempPostData({ ...tempPostData, ...update });
            }}
            isEditing={isEditing}
          />
          <OnboardingTourStep
            key="post-tour-step-3"
            flowType={OnboardingFlowType.NEWSLETTER_BLOG}
            target=".onboarding-post-details-target"
            content={
              <>
                You can set the post&apos;s access level to decide who is able
                to view it.
                <br />
                <br />
                We recommend adding a social preview image so your post displays
                a rich link preview when shared.
              </>
            }
            stepNumber={3}
            totalSteps={NEWSLETTER_BLOG_ONBOARDING_STEPS}
          />
        </>
      ) : (
        <CreateProductPublished productDataSettings={tempPostData} />
      )}
      <ConfirmDeleteModal
        heading="Are you sure you want to exit?"
        subtext="Your edits won't be saved. This cannot be undone."
        deleteButtonText="Exit"
        hideModal={!showConfirmBackModal}
        setIsVisible={setShowConfirmBackModal}
        deleteOnPress={backOnPress}
        icon={<Icon icon="ion:exit-outline" height={40} />}
      />
    </FullScreenContainer>
  );
};

export default memo(CreatePost);
